// -----------------------------------------------------------------------------
// User
// -----------------------------------------------------------------------------

export class BankingUserModel {
  client_id: string;
  finoos_user_id: string;
  name: string;
  automatic_analysis: string;
  user_scopes: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_synced_at: string;
}

export class BankingUserCreationRequestModel {
  client_id: string;
  name?: string;
  automatic_analysis?: string;
}

// -----------------------------------------------------------------------------
// Bank
// -----------------------------------------------------------------------------

export class BankingBankModel {
  id: string;
  finoOsBankId: string;
  bankCode: string;
  bic: string;
  bankName: string;
  logoUrl: string;
  created: string;
  displayOrder: number;
  newConsentRequired: boolean;
  accounts: BankingAccountModel[];
  selected?: boolean;
}

export class BankingBankResponseModel {
  banks: BankingBankModel[];
  totalCount: number;
}

export class BankingBankRequestModel {
  client_id: string;
  withAccounts?: boolean;
  types?: string[];
}

export class BankingDashboardDataModel {
  totalBalance: number;
  banks: BankingBankModel[];
  latestTransactions: BankingLatestTransactionsModel;
}

// -----------------------------------------------------------------------------
// Account
// -----------------------------------------------------------------------------

export type BankingAccountType =
  | 'GIRO'
  | 'TAGESGELD'
  | 'SPARBUCH'
  | 'KREDIT_KARTE'
  | 'KREDIT_KONTO'
  | 'DEPOT'
  | 'PAYPAL'
  | 'FESTGELD'
  | 'KREDITDARLEHEN'
  | 'FONDSDEPOT'
  | 'BAUSPAREN'
  | 'VERSICHERUNG'
  | 'UNKNOWN';

export class BankingOverdraftModel {
  amount: string;
  available: boolean;
}

export class BankingAccountModel {
  id: string;
  finoOsAccountId: string;
  accountNumber: string;
  balance: number;
  bankId: string;
  finoOsBankId: string;
  bankCode: string;
  bankName: string;
  bic: string;
  creditLine: number;
  overdraft: BankingOverdraftModel;
  currency: string;
  iban: string;
  icon: string;
  name: string;
  monthlySpendingLimit: number;
  owner: string;
  supportedPayments: string[];
  type: BankingAccountType;
  createdAt: string;
  updatedAt: string;
  isSharable: boolean;
  displayOrder: number;
  isVisible: boolean;
  /** Cashflow tab: false when the advisor left the account out of the analysis (default true) */
  includedInCashflow?: boolean;
  balanceWarningThreshold: number;
  transactionWarningThreshold: number;
  censoredPushNotification: boolean;
  selected?: boolean;
}

export class BankingAccountsResponseModel {
  accounts: BankingAccountModel[];
  totalCount: number;
}

export class BankingAccountThresholdsUpdateRequestModel {
  balanceWarningThreshold: number | null;
  transactionWarningThreshold: number | null;
}

export class BankingUpdateVisibilityRequestModel {
  accountId: string;
  isVisible: boolean;
}

// -----------------------------------------------------------------------------
// Transaction
// -----------------------------------------------------------------------------

export class BankingTransactionCategoryModel {
  id: number;
  level: number;
  name: string;
  title: string;
}

export class BankingTransactionPaymentPartnerModel {
  name: string;
  iban: string;
  bic: string;
  creditorId: string;
  logoUrl: string;
}

export class BankingTagModel {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export class BankingTransactionModel {
  id: string;
  accountId: string;
  finoOsTransactionId: string;
  amount: number;
  currency: string;
  bookingDate: string;
  valueDate: string;
  bookingText: string;
  purpose: string;
  merchantCategoryCode: string;
  mref: string;
  eref: string;
  transactionCode: number;
  sepaPurposeCode: string;
  paymentPartner: BankingTransactionPaymentPartnerModel;
  mainCategory: BankingTransactionCategoryModel;
  category: BankingTransactionCategoryModel;
  userTags: BankingTagModel[];
  createdAt: string;
  updatedAt: string;
  excludeFromAnalysis: boolean;
  bankLogoUrl: string;
  attachmentCount: number;
}

export class BankingTransactionAttachmentModel {
  id: string;
  type: string;
  fileUrl: string;
  previewUrl: string;
  filename: string;
  filesize: number;
}

export class BankingTransactionsResponseModel {
  transactions: BankingTransactionModel[];
  totalCount: number;
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
  hasNext: boolean;
}

export class BankingLatestTransactionsModel {
  page: number;
  pageSize: number;
  total: number;
  data: BankingTransactionModel[];
}

export class BankingTransactionRequestModel {
  accountIds?: string[] = [];
  analysisId?: string;
  paymentPartnerName?: string;
  query?: string;
  tagIds?: number[];
  finoOsTransactionId?: string;
  contractId?: string;
  from?: string;
  to?: string;
  minAmount?: number;
  maxAmount?: number;
  bgGroupId?: number;
  mainCategoryIds?: number[];
  hasAttachment?: boolean;
  page?: number = 0;
  size?: number = 50;
}

// -----------------------------------------------------------------------------
// Tag
// -----------------------------------------------------------------------------

export class BankingTagCreateUpdateModel {
  id?: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

// -----------------------------------------------------------------------------
// Category
// -----------------------------------------------------------------------------

export class BankingCategoryModel {
  id: number;
  name: string;
  title: string;
  level: number;
}

// -----------------------------------------------------------------------------
// Contract (service-to-service, accessed by clientId)
// -----------------------------------------------------------------------------

export class BankingContractPartnerModel {
  id: string;
  name: string;
  logo: string;
  squareLogo: string;
  logos: Record<string, string>;
  tags: string[];
  contact: Record<string, string>;
  address: Record<string, string>;
}

export class BankingContractModel {
  id: string;
  clientId: string;
  interval: string;
  contractNumber: string;
  finoOsContractId: string;
  amount: number;
  amountConsistency: string;
  category: string;
  transactionIds: string[];
  contractPartner: BankingContractPartnerModel;
  createdAt: string;
}

export class BankingContractsResponseModel {
  insuranceContracts: BankingContractModel[];
  otherContracts: BankingContractModel[];
}

// -----------------------------------------------------------------------------
// Securities
// -----------------------------------------------------------------------------

export class BankingSecurityModel {
  id: string;
  accountId: string;
  name: string;
  isin: string;
  wkn: string;
  amount: number;
  amountOriginalCurrency: number;
  currency: string;
  exchangeRate: number;
  market: string;
  price: number;
  priceCurrency: string;
  purchasePrice: number;
  purchasePriceCurrency: string;
  quantity: number;
  percentage: number;
  logoUrl: string;
}

export class BankingDepotDashboardResponseModel {
  totalBalance: number;
  banks: BankingBankModel[];
  securities: BankingSecurityModel[];
}

// -----------------------------------------------------------------------------
// Bank logins (session)
// -----------------------------------------------------------------------------

export class BankingBankLoginItemModel {
  bank_login_id: string;
  active: boolean;
  last_synchronization: number;
  status: string;
}

export class BankingBankLoginsResponseModel {
  logins: BankingBankLoginItemModel[];
}

// ===== Analysis =====
export class BankingAnalysisModel {
  id: string;
  name: string;
  accountIds: string[];
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export class BankingAnalysisCreateRequestModel {
  name: string;
  accountIds: string[];
}

// ===== Analysis Report - REQUEST =====
export class BankingAnalysisReportRequestModel {
  accountId?: string;
  analysisId?: string;
  balanceStatisticsFrom?: string; // YYYY-MM-DD
  monthlyExpensesStatistics?: DateRangeDto;
  monthlyTurnoverStatistics?: DateRangeDto;
  categoryStatistics?: DateRangeDto;
  incomeOutcomeStatistics?: DateRangeDto;
}

export class DateRangeDto {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

// ===== Analysis Report - RESPONSE =====
export class BankingAnalysisReportResponseModel {
  accounts: BankingAccountModel[];
  statistics: StatisticsDto;
}

export class AccountOverviewDto {
  id: string;
  name: string;
  iban: string;
  bankIconUrl: string;
  balance: number;
  type: string;
  order: number;
  visible: boolean;
  isSharable: boolean;
  selected?: boolean;
}

export class StatisticsDto {
  balance: BalanceStatisticsDto;
  monthlyExpenses: MonthlyExpensesStatisticsDto;
  category: CategoryStatisticsDto;
  monthlyTurnover: MonthlyTurnoverStatisticsDto;
  incomeOutcome: IncomeOutcomeStatisticsDto;
}

export class BalanceStatisticsDto {
  dataPoints: DateDataPointDto[];
}

export class DateDataPointDto {
  label: string; // YYYY-MM-DD
  value: number;
}

export class MonthlyExpensesStatisticsDto {
  dataPoints: MonthDataPointDto[];
  averageExpenses: number;
}

export class MonthDataPointDto {
  label: string;
  value: number;
}

export class FormattedMonthlyExpanses {
  dates: string[];
  numbers: number[];
}

export class CategoryStatisticsDto {
  dataPoints: CategorySpendingDto[];
  totalSpending?: number;
  transactionCount?: number;
}

export class CategorySpendingDto {
  category: BankingFinoOsCategoryDto;
  amount: number;
  percentage?: number;
  transactionCount?: number;
  averageAmount?: number;
}

export class MonthlyTurnoverStatisticsDto {
  dataPoints: MonthlyTurnoverPointDto[];
}

export class MonthlyTurnoverPointDto {
  label: string;
  income: number;
  outcome: number;
}

export class IncomeOutcomeStatisticsDto {
  totalIncome: number;
  totalOutcome: number;
}

export class BankingFinoOsCategoryDto {
  bgGroup: number;
  bgGroupName: string;
}

// ===== Analysis Report - SETTINGS =====
export class BankingAnalysisReportSettingsModel {
  id: string;
  clientId: string;
  spendingByCategoryEnabled: boolean;
  incomeAnalysisEnabled: boolean;
  contractsEnabled: boolean;
  balanceHistoryEnabled: boolean;
  allowedAccountIds: string[];
  allowedCategories: string[];
  createdAt: string;
  updatedAt: string;
}

export class BankingAnalysisReportSettingsRequestModel {
  spendingByCategoryEnabled?: boolean;
  incomeAnalysisEnabled?: boolean;
  contractsEnabled?: boolean;
  balanceHistoryEnabled?: boolean;
  allowedCategories?: string[];
  allowedAccountIds?: string[];
}

// ===== Cashflow Analysis (B2B Cashflow tab, FIN-7165) =====
export type IncomeDistributionType = 'LIABILITIES' | 'PROTECTION' | 'RETIREMENT_AND_WEALTH' | 'FREE';

export class CashflowSummaryDto {
  totalIncome: number;
  totalSpending: number;
  totalCashflow: number;
  averageIncome: number;
  averageSpending: number;
  averageCashflow: number;
  savingsRate: number;
  positiveMonths: number;
  negativeMonths: number;
}

export class IncomeDistributionItemDto {
  type: IncomeDistributionType;
  displayName: string;
  monthlyAmount: number;
  percentage: number;
}

export class IncomeDistributionDto {
  basis: number;
  items: IncomeDistributionItemDto[];
}

export class LiquidityReserveDto {
  currentReserve: number;
  targetReserve: number;
  targetMonths: number;
  monthsCovered: number;
  reached: boolean;
}

export class IncomeCategoryDto {
  category: string;
  displayName: string;
  total: number;
  monthlyAverage: number;
  percentage: number;
  transactionCount: number;
}

export class IncomeStructureDto {
  totalIncome: number;
  monthlyAverage: number;
  categories: IncomeCategoryDto[];
}

export class CashflowAnalysisDto {
  bankConnected: boolean;
  months: number;
  from: string;
  to: string;
  summary: CashflowSummaryDto;
  monthlyTurnover: MonthlyTurnoverStatisticsDto;
  incomeOutcome: IncomeOutcomeStatisticsDto;
  categories: CategoryStatisticsDto;
  incomeStructure: IncomeStructureDto;
  incomeDistribution: IncomeDistributionDto;
  liquidity: LiquidityReserveDto;
  accounts: BankingAccountModel[];
}

// ===== Client events (FIN-7177, banking rules over the bank data) =====
export type ClientEventType =
  | 'SALARY_CHANGE'
  | 'LIQUIDITY_PRESSURE'
  | 'ONE_OFF_INFLOW'
  | 'RENT_CHANGE'
  | 'NEW_CONSUMER_CREDIT'
  | 'NEW_EMPLOYER'
  | 'NEW_RECURRING_INCOME'
  | 'PROPERTY_FINANCING';
export type ClientEventStatus = 'NEW' | 'SEEN' | 'DONE' | 'DISMISSED';

export class ClientEventDto {
  id: string;
  clientId: string;
  type: ClientEventType;
  displayName: string;
  status: ClientEventStatus;
  period: string;
  detectedAt: string;
  headline: string;
  evidence: Record<string, unknown>;
}

export class ClientOpenEventsDto {
  clientId: string;
  openCount: number;
  latestDetectedAt: string;
  latestEvent: ClientEventDto | null;
}
